import { useState, useEffect } from "react";
import { ArrowLeft, Lock, Loader2, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/uiDir/card";
import { Input } from "@/components/uiDir/input";
import { Label } from "@/components/uiDir/label";
import { Button } from "@/components/uiDir/button";
import { Alert, AlertDescription } from "@/components/uiDir/alert";
import { toast } from "sonner";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "@/lib/authService"; 

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

 
  useEffect(() => {
   
    const token = searchParams.get("token") || 
                  searchParams.get("code") || 
                  searchParams.get("reset_token") ||
                  searchParams.get("access_token");
    
    console.log("All URL params:", Object.fromEntries(searchParams.entries()));
    console.log("Found token:", token);
    
    if (!token) {
      toast.error("Invalid reset link. Please request a new password reset.");
      setStatusMessage("❌ Invalid or missing reset token. Please check your email for the correct link.");
    } else {
      setResetToken(token);
      setStatusMessage(""); 
    }
  }, [searchParams]);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long.";
    }
    if (!/[A-Z]/.test(password)) {
      return "Password must contain at least one uppercase letter.";
    }
    if (!/[a-z]/.test(password)) {
      return "Password must contain at least one lowercase letter.";
    }
    if (!/[0-9]/.test(password)) {
      return "Password must contain at least one number.";
    }
    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!resetToken) {
      toast.error("Invalid reset link. Please request a new password reset.");
      return;
    }

    if (!newPassword.trim() || !confirmPassword.trim()) {
      toast.error("Please enter and confirm your new password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }


    const validationError = validatePassword(newPassword);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsSubmitting(true);
    setStatusMessage("");

    try {
   
      const { error } = await resetPassword(resetToken, newPassword);

      if (error) {
        toast.error(error.message);
        setStatusMessage("❌ " + error.message);
        return;
      }

      const message = "Password reset successfully! Redirecting to login...";
      toast.success(message);
      setStatusMessage("✅ " + message);
      setIsSuccess(true);

      setNewPassword("");
      setConfirmPassword("");

      setTimeout(() => {
        navigate("/login");
      }, 2000);

    } catch (err) {
      console.error("Password reset error:", err);
      const message = "Something went wrong while resetting your password. Please try again.";
      toast.error(message);
      setStatusMessage("❌ " + message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to login
        </Link>

        <Card className="glass-card">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Reset Password</CardTitle>
            <CardDescription>
              Enter your new password below to reset your account password.
            </CardDescription>
          </CardHeader>

          <CardContent>
            {isSuccess ? (
              <div className="text-center py-8 space-y-4">
                <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
                <p className="text-lg font-medium">Password Reset Successful!</p>
                <p className="text-sm text-muted-foreground">
                  Redirecting you to login...
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder="Enter your new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-9"
                      required
                      disabled={isSubmitting || !resetToken}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-9"
                      required
                      disabled={isSubmitting || !resetToken}
                    />
                  </div>
                </div>

                <div className="text-xs text-muted-foreground space-y-1">
                  <p className="font-medium">Password must contain:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>At least 8 characters</li>
                    <li>One uppercase letter</li>
                    <li>One lowercase letter</li>
                    <li>One number</li>
                  </ul>
                </div>

                {statusMessage && (
                  <Alert className={statusMessage.startsWith("✅") ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
                    <AlertDescription className="text-sm">{statusMessage}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2"
                  size="lg"
                  disabled={isSubmitting || !resetToken}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Resetting password...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </form>
            )}

            <p className="text-center text-sm text-muted-foreground mt-4">
              Remembered your password?{" "}
              <Link to="/login" className="text-primary font-medium hover:underline">
                Log in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;